'use client';

import type { CategoriesEntity } from '@eliadmin/core/src/api/general/types/general-layout-types';
import { SocialIcons } from '@eliadmin/core/src/components/header/social-icons';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetTrigger,
} from '@eliadmin/core/src/components/ui/sheet';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { getSafeHref, isRelativeHref } from '@eliadmin/core/src/utils/url-utils';
import Link from '@eliadmin/core/src/components/link';
import { useState } from 'react';
import { BiMenuAltRight } from 'react-icons/bi';
import { MdOutlineSell, MdSort } from 'react-icons/md';
import { RiAsterisk } from 'react-icons/ri';
import { useHeaderCategoryChildren } from './use-header-category-children';

/** Body M from the design system — shared by every row in the sidebar. */
const BODY_M = 'tracking-[-0.48px] text-[#111827]';
const BODY_M_BOLD = cn(BODY_M, 'text-base font-bold leading-[1.2]');
const BODY_M_REGULAR = cn(BODY_M, 'text-base font-normal leading-[1.3]');

function hasCategoryChildren(category?: CategoriesEntity): boolean {
  if (!category) return false;
  if (typeof category.has_childs === 'boolean') return category.has_childs;
  if (Array.isArray(category.childs)) return category.childs.length > 0;

  // If an older API omits both fields, a harmless lazy request is safer than
  // permanently hiding real children from the navigation.
  return true;
}

/**
 * One category row inside the "Katalóg produktov" tree. Children are fetched
 * lazily through the same route the desktop catalogue uses, so opening the
 * sidebar never pulls the whole tree down at once.
 */
function SidebarCategory({ category, depth = 0 }: { category: CategoriesEntity; depth?: number }) {
  const locale = useLocaleStore(state => state.currentLocale);
  const [isExpanded, setIsExpanded] = useState(false);

  const localChildren = category.childs ?? [];
  const hasChildren = hasCategoryChildren(category);

  const childrenQuery = useHeaderCategoryChildren({
    locale,
    parentId: category.id,
    enabled: isExpanded && hasChildren && localChildren.length === 0,
  });

  const children = localChildren.length > 0 ? localChildren : (childrenQuery.data ?? []);
  const isLoadingChildren = localChildren.length === 0 && childrenQuery.isPending;

  return (
    <AccordionItem
      value={String(category.id ?? category.slug)}
      className={cn('border-t-0', 'ea-mobile-sidebar__category')}
    >
      <div className="flex items-center justify-between gap-2">
        <SheetClose asChild>
          <Link
            prefetchOnHover
            href={ROUTES.CATEGORY({ slug: category.slug })}
            prefetch={false}
            className={cn(
              'flex-1 py-2 text-left',
              BODY_M_REGULAR,
              depth > 0 && 'text-sm',
              'ea-mobile-sidebar__category-link',
            )}
          >
            {category.name}
          </Link>
        </SheetClose>
        {hasChildren && (
          <AccordionTrigger
            className="w-8 flex-none justify-center py-2 [&>svg]:text-[#111827]"
            aria-label={category.name}
            onClick={() => setIsExpanded(current => !current)}
          />
        )}
      </div>
      {hasChildren && (
        <AccordionContent className="pb-0 pl-4 pt-0">
          <Accordion type="single" collapsible>
            {isLoadingChildren
              ? Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="my-3 h-4 w-2/3 animate-pulse rounded bg-muted" />
                ))
              : children.map((child, index) => (
                  <SidebarCategory
                    key={child.id ?? child.slug ?? index}
                    category={child}
                    depth={depth + 1}
                  />
                ))}
          </Accordion>
        </AccordionContent>
      )}
    </AccordionItem>
  );
}

export type MobileSidebarProps = {
  className?: {
    trigger?: string;
    content?: string;
    firstSeparator?: string;
    menuList?: string;
    menuLink?: string;
    menuGroup?: string;
    menuSeparator?: string;
    buttonWrapper?: string;
    button?: string;
    iconsWrapper?: string;
    icon?: string;
  };
  side?: 'right' | 'left' | 'bottom' | 'top';
  firstSeparator?: boolean;
  menu?: {
    type: 'page';
    name: string;
    path?: string;
  }[];
  menuArrow?: boolean;
  menuSeparator?: boolean;
  categories?: CategoriesEntity[];
};

const MobileSidebar = (props: MobileSidebarProps) => {
  const { t } = useTranslations();

  const categories = props.categories ?? [];

  return (
    <Sheet>
      <SheetTrigger
        aria-label={t('text3241')}
        className={cn('text-[#111827]', 'ea-mobile-sidebar__trigger', props.className?.trigger)}
      >
        <BiMenuAltRight size={24} />
      </SheetTrigger>
      <SheetContent
        side={props.side}
        className={cn(
          'flex w-full max-w-[358px] flex-col justify-between gap-0 overflow-y-auto bg-white px-4 pb-6 pt-24',
          'ea-mobile-sidebar__content',
          props.className?.content,
        )}
        closeIcon={{
          wrapper: 'left-4 top-6 opacity-100',
          icon: 'h-6 w-6 text-[#111827]',
        }}
      >
        <nav className={cn('flex flex-col gap-6', 'ea-mobile-sidebar__menu')}>
          <div className={cn('flex flex-col gap-6', props.className?.menuGroup)}>
            <Accordion type="single" collapsible>
              <AccordionItem value="catalogue" className="border-t-0">
                <AccordionTrigger
                  className={cn('justify-start gap-1 py-0 [&>svg]:text-[#111827]', BODY_M_BOLD)}
                >
                  <span className="flex items-center gap-1">
                    <MdSort size={20} className="shrink-0 -scale-x-[1]" />
                    {t('text3316')}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-0 pt-2">
                  <Accordion type="single" collapsible>
                    {categories.map((category, index) => (
                      <SidebarCategory
                        key={category.id ?? category.slug ?? index}
                        category={category}
                      />
                    ))}
                  </Accordion>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <SheetClose asChild>
              <Link
                prefetchOnHover
                href="/akcne-produkty"
                prefetch={false}
                className={cn('flex items-center gap-1', BODY_M_BOLD)}
              >
                <MdOutlineSell size={20} className="shrink-0" />
                {t('text3756')}
              </Link>
            </SheetClose>

            <SheetClose asChild>
              <Link
                prefetchOnHover
                href="/saprem-expert"
                prefetch={false}
                className={cn('flex items-center gap-1', BODY_M_BOLD)}
              >
                <RiAsterisk size={20} className="shrink-0" />
                Saprem Expert
              </Link>
            </SheetClose>
          </div>

          {!!props.menu?.length && (
            <>
              <div
                className={cn(
                  'h-px w-full bg-[#E5E7EB]',
                  'ea-mobile-sidebar__separator',
                  props.className?.menuSeparator,
                )}
              />
              {props.menu.map((item, index) => {
                const href = item.path ? getSafeHref(item.path) : '';
                const Comp = item.path ? (isRelativeHref(href) ? Link : 'a') : 'span';

                return (
                  <SheetClose key={index} asChild>
                    <Comp
                      href={href}
                      prefetch={false}
                      className={cn(
                        'w-fit',
                        BODY_M_REGULAR,
                        'ea-mobile-sidebar__menu-link',
                        props.className?.menuLink,
                      )}
                    >
                      {item.name}
                    </Comp>
                  </SheetClose>
                );
              })}
            </>
          )}
        </nav>

        <SocialIcons
          className={{
            wrapper: cn('flex items-center gap-3 pt-6', props.className?.iconsWrapper),
            button: cn(
              'max-h-12 min-h-12 min-w-12 max-w-12 rounded-full border border-[#E5E7EB] bg-white shadow-none hover:bg-neutral-50',
              props.className?.button,
            ),
            iconLink: 'bg-transparent',
            icon: cn('h-5 w-5 fill-[#111827] text-[#111827]', props.className?.icon),
          }}
        />
      </SheetContent>
    </Sheet>
  );
};

export default MobileSidebar;
